import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { authService } from '../services/authService';
import { toast } from 'react-toastify';
import { useTranslation } from '../hooks/useTranslation';

const VerifyEmail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, locale, setLocale } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [email, setEmail] = useState('');
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();
    const inputRefs = useRef([]);

    useEffect(() => {
        // Get email from location state or localStorage
        const emailFromState = location.state?.email;
        const user = authService.getCurrentUser();
        const emailFromUser = user?.email;

        const emailAddress = emailFromState || emailFromUser || '';
        setEmail(emailAddress);
    }, [location]);

    const handleCodeChange = (index, value) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) {
            return;
        }

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all 6 digits are entered
        if (newCode.every(digit => digit !== '') && index === 5) {
            handleVerify(newCode.join(''));
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();
        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split('');
            setCode(digits);
            // Focus last input
            inputRefs.current[5]?.focus();
            // Auto-submit
            setTimeout(() => {
                handleVerify(pastedData);
            }, 100);
        }
    };

    const handleVerify = async (verificationCode = null) => {
        const codeToVerify = verificationCode || code.join('');

        if (codeToVerify.length !== 6) {
            toast.error(t('verifyEmail.enterVerificationCode'));
            return;
        }

        if (!email) {
            toast.error(t('verifyEmail.emailNotFound'));
            return;
        }

        try {
            setLoading(true);
            const response = await authService.verifyEmail(email, codeToVerify);
            if (response.success) {
                toast.success(t('verifyEmail.emailVerifiedSuccessfully'));

                // If user data is returned, update localStorage
                if (response.data?.user) {
                    localStorage.setItem('user', JSON.stringify(response.data.user));

                    // If token is provided, store it and redirect based on role
                    if (response.data?.token) {
                        localStorage.setItem('auth_token', response.data.token);
                        const redirectPath = getRoleBasedRedirect(response.data.user?.role);
                        navigate(redirectPath);
                    } else {
                        // No token, redirect to login
                        navigate('/login');
                    }
                } else {
                    // No user data, redirect to login
                    navigate('/login');
                }
            } else {
                toast.error(t('verifyEmail.emailVerificationFailed'));
            }
        } catch (error) {
            console.error('Verify email error:', error);
            const message = error.response?.data?.message || error.message || t('verifyEmail.emailVerificationFailed');
            toast.error(message);
            // Clear code on error
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!email) {
            toast.error(t('verifyEmail.emailNotFound'));
            return;
        }

        try {
            setResending(true);
            const response = await authService.resendEmailVerification(email);
            if (response.success) {
                toast.success(t('verifyEmail.resendVerificationCodeSuccessfully'));
                // Clear code inputs
                setCode(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
            } else {
                toast.error(t('verifyEmail.resendVerificationCodeFailed'));
            }
        } catch (error) {
            console.error('Resend verification error:', error);
            const message = error.response?.data?.message || error.message || t('verifyEmail.resendVerificationCodeFailed');
            toast.error(message);
        } finally {
            setResending(false);
        }
    };

    const getRoleBasedRedirect = (role) => {
        const roleRedirects = {
            employee: '/employee/home',
            company: '/company/vacancies',
            recruitment_company: '/company/vacancies',
            admin: '/dashboard',
        };
        return roleRedirects[role] || '/employee/home';
    };

    const toggleLanguage = () => {
        const newLocale = locale === 'ar' ? 'en' : 'ar';
        setLocale(newLocale);
    };

    return (
        <div className="login-page">
            {/* Language Switcher */}
            <div style={{
                position: 'absolute',
                top: '20px',
                right: locale === 'ar' ? '20px' : 'auto',
                left: locale === 'en' ? '20px' : 'auto',
                zIndex: 1000
            }}>
                <button
                    onClick={toggleLanguage}
                    style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#333',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                >
                    <span>🌐</span>
                    <span>{locale === 'ar' ? 'EN' : 'ع'}</span>
                </button>
            </div>

            {/* Header with gradient */}
            <div className="login-header">
                <div className="login-header-content text-center">
                    <img src="/images/logo.png" alt="logo" className="login-header-logo" width={135} height={135} />
                    <h1 className="app-title">TAWZEEF</h1>
                </div>
            </div>

            {/* Main verify card */}
            <div className="login-card">
                <h2 className="welcome-text">{t('verifyEmail.verifyEmailTitle')}</h2>
                <p className="verify-phone-description" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                    {t('verifyEmail.verificationCodeSentToEmail')}<br />
                    <strong>{email || t('verifyEmail.email')}</strong>
                </p>

                <form onSubmit={(e) => { e.preventDefault(); handleVerify(); }} className="login-form" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                    {/* Verification Code Input */}
                    <div className="form-group-rtl">
                        <label className="form-label-rtl">{t('verifyEmail.verificationCode')}</label>
                        <div className="verification-code-container">
                            {code.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleCodeChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={index === 0 ? handlePaste : undefined}
                                    className="verification-code-input"
                                    autoFocus={index === 0}
                                />
                            ))}
                        </div>
                    </div>
                    {/* User Email Field if not provided in state */}
                    <div className={`form-group-rtl ${email ? 'd-none' : ''}`}>
                        <label htmlFor="email" className="form-label-rtl">{t('verifyEmail.email')}</label>
                        <input
                            type="email"
                            id="email"
                            className="password-input form-control p-3"
                            placeholder={t('verifyEmail.emailPlaceholder')}
                            {...register('email', {
                                required: t('verifyEmail.emailRequired'),
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: t('verifyEmail.emailInvalid'),
                                },
                            })}
                        />
                        {errors.email && <span className="error-message-rtl">{errors.email.message}</span>}
                    </div>

                    {/* Resend Code Link */}
                    <div className="resend-code-container" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={resending}
                            className="resend-code-link"
                        >
                            {resending ? t('verifyEmail.resendingVerificationCode') : t('verifyEmail.resendVerificationCode')}
                        </button>
                    </div>

                    {/* Verify Button */}
                    <button
                        type="submit"
                        disabled={loading || code.some(digit => !digit)}
                        className="login-button"
                    >
                        {loading ? t('verifyEmail.verifyingCode') : t('verifyEmail.verify')}
                    </button>

                    {/* Back to Login Link */}
                    <p className="create-account-link">
                        {t('verifyEmail.didNotReceiveCode')} {' '}
                        <Link to="/login">{t('verifyEmail.returnToLogin')}</Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default VerifyEmail;

